import Bugsnag from '@bugsnag/js'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Layout from '../components/Layout'
import StripeModal, { StripeSession } from '../components/StripeModal'
import { client } from '../utils/client'
import { FormData, NormalFormData, StepType } from '../utils/sharedTypes'
import { learningPhoneNumber, santePhoneNumber } from '../utils/constants'
import { useLegalEntity } from '../hooks/useLegalEntity'
import { tunnelBrand } from '../utils/tunnelBrand'

type OverviewInstallment = {
  id: number
  index: number
  due_date: string
  payment_date: string | null
  amount: number
  status: string
  payment_provider: string
  provider_label: string
  pay_now_eligible: boolean
  is_past: boolean
}

type PaymentOverview = {
  product_name: string | null
  is_gocardless: boolean
  schedule: {
    id: number | null
    cancelled: boolean
    total_amount: number | null
    remaining_amount: number
    installments_count: number
    recurring_payment_day: number | null
  } | null
  next_installment: {
    id: number
    due_date: string
    amount: number
    provider_label: string
    status: string
  } | null
  card: {
    registered: boolean
    brand: string | null
    last4: string | null
  }
  mandate: {
    status: string | null
    ready: boolean
    bank_name: string | null
    account_holder_name: string | null
    account_number_ending: string | null
    scheme: string | null
    next_possible_charge_date: string | null
    earliest_safe_charge_date: string | null
  } | null
  installments: OverviewInstallment[]
}

const MANDATE_POLL_MS = 2000
const MANDATE_POLL_TIMEOUT_MS = 30_000

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount)

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

const statusLabel = (status: string, provider?: string): { label: string; color: string } => {
  const isSepa = (provider || '').toLowerCase() === 'gocardless'
  switch (status) {
    case 'PAID':
      return { label: 'Payé', color: 'success.main' }
    case 'VALIDATED':
      return { label: isSepa ? 'Prélèvement confirmé' : 'Validé', color: 'success.main' }
    case 'REFUNDED':
      return { label: 'Remboursé', color: 'success.main' }
    case 'FAILED':
      return { label: 'Échoué', color: 'error.main' }
    case 'CANCELLED':
      return { label: 'Annulé', color: 'error.main' }
    case 'SUBMITTED':
      return {
        label: isSepa ? 'Prélèvement en cours' : 'En cours',
        color: 'warning.main',
      }
    case 'RETRYING':
      return { label: 'En cours', color: 'warning.main' }
    case 'SUSPENDED':
      return { label: 'Suspendu', color: 'warning.main' }
    case 'WAITING':
    case 'DRAFT':
    default:
      return { label: 'À venir', color: 'text.secondary' }
  }
}

const alertSx = (severity: 'warning' | 'error' | 'info' | 'success') => {
  const accents = {
    warning: { border: '#ED6C02', bg: '#FFF4E5' },
    error: { border: '#D32F2F', bg: '#FDECEA' },
    info: { border: '#0288D1', bg: '#E5F6FD' },
    success: { border: '#007A59', bg: '#E8F5F0' },
  }[severity]
  return {
    borderLeft: `6px solid ${accents.border}`,
    backgroundColor: accents.bg,
    alignItems: 'center',
    py: 2,
    px: 2.5,
    '& .MuiAlert-message': { width: '100%' },
    '& .MuiAlert-icon': { fontSize: 28, mr: 1.5 },
  }
}

const UpdatePaymentMethod = () => {
  const theme = useTheme()
  const [stripeSession, setStripeSession] = useState<StripeSession>()
  const [open, setOpen] = useState(false)
  const [loadingCard, setLoadingCard] = useState(false)
  const [payingId, setPayingId] = useState<number | null>(null)
  const [mandateLoading, setMandateLoading] = useState(false)
  const [mandateConfirming, setMandateConfirming] = useState(false)
  const [mandateTimedOut, setMandateTimedOut] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [registrationData, setRegistrationData] = useState<NormalFormData | null>(null)
  const [overview, setOverview] = useState<PaymentOverview | null>(null)
  const [overviewError, setOverviewError] = useState<string | null>(null)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  const global_id = new URLSearchParams(location.search).get('global_id')
  const mandateReturn = new URLSearchParams(location.search).get('mandate_return') === '1'

  const mockForm: FormData = {
    global_id: global_id || '',
    first_name: registrationData?.first_name || '',
    last_name: registrationData?.last_name || '',
    mobile_phone: registrationData?.mobile_phone || '',
    email: registrationData?.email || '',
    entrypoint_url: null,
    lead_source: null,
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    theme: registrationData?.theme || 'learning',
    product: registrationData?.product || null,
    products: registrationData?.products || [],
  }

  const mockSteps: StepType[] = [
    {
      url: 'mon-echeancier',
      label: 'Mon échéancier',
    },
  ]

  const phoneNumber =
    mockForm.theme === 'sante' ? santePhoneNumber : learningPhoneNumber
  // Sur inscription.edumiam.com : l'email de contact d'Edumiam (JSON des entités juridiques), pas de numéro.
  const edumiamEmail = useLegalEntity('edumiam').contacts.email
  const contact = tunnelBrand() ? (
    edumiamEmail ? (
      <a href={`mailto:${edumiamEmail}`}>{edumiamEmail}</a>
    ) : (
      <a href="https://www.edumiam.com/contact-pro">notre page de contact</a>
    )
  ) : (
    <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
  )

  const fetchOverview = useCallback(async () => {
    if (!global_id) return null
    const resp = await client.get(`/registration/${global_id}/payment_overview/`)
    const data = resp.data as PaymentOverview
    setOverview(data)
    return data
  }, [global_id])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    setOpen(Boolean(stripeSession))
  }, [stripeSession])

  useEffect(() => {
    if (!global_id) {
      setIsLoadingData(false)
      return
    }

    setIsLoadingData(true)
    setOverviewError(null)

    const registrationPromise = client
      .get(`/registration/${global_id}/`)
      .then((registrationResp) => {
        const data = registrationResp.data as NormalFormData
        setRegistrationData(data)
        if (!data.stripe_setup_at) {
          navigate(`/recapitulatif?global_id=${global_id}`)
        }
      })

    const overviewPromise = fetchOverview().catch((err: Error) => {
      Bugsnag.notify(err)
      console.error(err)
      setOverviewError("Impossible de charger l'échéancier. Veuillez réessayer.")
      return null
    })

    Promise.all([registrationPromise, overviewPromise])
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
      .finally(() => {
        setIsLoadingData(false)
      })
  }, [global_id, navigate, fetchOverview])

  // After GoCardless redirect: poll until mandate is ready.
  useEffect(() => {
    if (!mandateReturn || !global_id || !overview?.is_gocardless) return
    if (overview.mandate?.ready) {
      setMandateConfirming(false)
      setActionSuccess('Votre mandat de prélèvement SEPA est bien configuré.')
      return
    }
    if (mandateTimedOut) return

    setMandateConfirming(true)
    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      void fetchOverview()
        .then((data) => {
          if (data?.mandate?.ready) {
            setMandateConfirming(false)
            setActionSuccess('Votre mandat de prélèvement SEPA est bien configuré.')
            window.clearInterval(timer)
          } else if (Date.now() - startedAt >= MANDATE_POLL_TIMEOUT_MS) {
            setMandateConfirming(false)
            setMandateTimedOut(true)
            window.clearInterval(timer)
          }
        })
        .catch((err: Error) => {
          Bugsnag.notify(err)
        })
    }, MANDATE_POLL_MS)

    return () => window.clearInterval(timer)
  }, [mandateReturn, global_id, overview?.is_gocardless, overview?.mandate?.ready, mandateTimedOut, fetchOverview])

  const pastInstallments = useMemo(
    () => overview?.installments.filter((item) => item.is_past) || [],
    [overview],
  )
  const upcomingInstallments = useMemo(
    () => overview?.installments.filter((item) => !item.is_past) || [],
    [overview],
  )

  const handleUpdatePaymentMethod = () => {
    if (!global_id) return
    setLoadingCard(true)
    setActionError(null)
    client
      .post(`/registration/${global_id}/create_stripe_checkout_session/`, mockForm)
      .then((resp: { data: StripeSession }) => {
        setStripeSession(resp.data)
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        setActionError("Impossible d'ouvrir la mise à jour de carte. Réessayez dans un instant.")
      })
      .finally(() => {
        setLoadingCard(false)
      })
  }

  const handlePayNow = (installmentId: number) => {
    if (!global_id) return
    setPayingId(installmentId)
    setActionError(null)
    setActionSuccess(null)
    client
      .post(`/registration/${global_id}/pay_installment/`, { installment_id: installmentId })
      .then((resp: { data: { success: boolean; overview?: PaymentOverview; message?: string } }) => {
        if (resp.data.overview) {
          setOverview(resp.data.overview)
        } else {
          void fetchOverview()
        }
        setActionSuccess('Paiement enregistré. Merci !')
      })
      .catch((err: { response?: { data?: { message?: string; detail?: string | string[] } } }) => {
        Bugsnag.notify(err as Error)
        const detail = err.response?.data?.detail
        const detailMessage = Array.isArray(detail) ? detail[0] : detail
        setActionError(
          err.response?.data?.message ||
            detailMessage ||
            "Le paiement n'a pas abouti. Vérifiez votre carte ou réessayez plus tard.",
        )
        void fetchOverview()
      })
      .finally(() => {
        setPayingId(null)
      })
  }

  const startMandateFlow = (force = false) => {
    if (!global_id) return
    setMandateLoading(true)
    setActionError(null)
    setMandateTimedOut(false)
    client
      .post(`/registration/${global_id}/create_gocardless_mandate_flow/`, {
        return_to: 'schedule',
        force,
      })
      .then((resp: { data: { redirect_url: string | null } }) => {
        if (resp.data.redirect_url) {
          window.location.href = resp.data.redirect_url
        } else {
          setActionError('Impossible de démarrer la configuration du mandat SEPA.')
        }
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        setActionError('Une erreur est survenue lors de la configuration du mandat SEPA.')
      })
      .finally(() => {
        setMandateLoading(false)
      })
  }

  if (isLoadingData) {
    return (
      <Layout form={mockForm} steps={mockSteps} wideContent>
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
          <CircularProgress />
        </Grid>
      </Layout>
    )
  }

  if (!registrationData) {
    return (
      <Layout form={mockForm} steps={mockSteps} wideContent>
        <Grid container spacing={3} sx={{ width: '100%', padding: 3 }}>
          <Grid item xs={12}>
            <Typography variant="h4" color="error" gutterBottom>
              Inscription non trouvée
            </Typography>
            <Typography variant="body1">
              Impossible de trouver l&apos;inscription correspondante. Vérifiez le lien reçu par e-mail.
            </Typography>
          </Grid>
        </Grid>
      </Layout>
    )
  }

  if (!overview) {
    return (
      <Layout form={mockForm} steps={mockSteps} wideContent>
        <Grid container spacing={3} sx={{ width: '100%', padding: 3 }}>
          <Grid item xs={12}>
            <Alert severity="error" sx={alertSx('error')}>
              {overviewError || "Impossible de charger l'échéancier. Veuillez réessayer."}
            </Alert>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={() => {
                setIsLoadingData(true)
                setOverviewError(null)
                fetchOverview()
                  .catch((err: Error) => {
                    Bugsnag.notify(err)
                    setOverviewError("Impossible de charger l'échéancier. Veuillez réessayer.")
                  })
                  .finally(() => setIsLoadingData(false))
              }}
            >
              Réessayer
            </Button>
          </Grid>
        </Grid>
      </Layout>
    )
  }

  const mandateMissing = overview.is_gocardless && !overview.mandate?.ready
  const dueFailures = upcomingInstallments.filter((item) => item.status === 'FAILED')
  const sepaInFlight = upcomingInstallments.filter(
    (item) =>
      item.payment_provider.toLowerCase() === 'gocardless' &&
      ['SUBMITTED', 'VALIDATED'].includes(item.status),
  )
  const remainingInstallments = overview.installments.filter(
    (item) => !['PAID', 'CANCELLED', 'REFUNDED'].includes(item.status),
  )
  const hasRemainingCardInstallment = remainingInstallments.some(
    (item) => item.payment_provider.toLowerCase() === 'stripe',
  )
  // Once only SEPA installments remain, hide the card management block — pay-now stays on rows.
  const showCardSection =
    !overview.is_gocardless || hasRemainingCardInstallment || !overview.card.registered
  const showPaymentMethodsSection = overview.is_gocardless || showCardSection

  const cardLabel = overview.card.last4
    ? `${overview.card.brand ? overview.card.brand.charAt(0).toUpperCase() + overview.card.brand.slice(1) : 'Carte'} •••• ${overview.card.last4}`
    : overview.card.registered
      ? 'Carte enregistrée'
      : 'Aucune carte'

  const paymentMethodSummary = (() => {
    if (overview.is_gocardless && !hasRemainingCardInstallment) {
      return {
        title: overview.mandate?.ready
          ? overview.mandate.bank_name
            ? `${overview.mandate.bank_name}${
                overview.mandate.account_number_ending
                  ? ` · •••• ${overview.mandate.account_number_ending}`
                  : ''
              }`
            : 'Prélèvements SEPA'
          : 'Prélèvements SEPA',
        subtitle: overview.mandate?.ready ? 'Mandat actif' : 'Mandat à configurer',
      }
    }
    return {
      title: cardLabel,
      subtitle: overview.is_gocardless
        ? overview.mandate?.ready
          ? 'Carte + prélèvements SEPA'
          : 'Carte enregistrée · SEPA à configurer'
        : 'Paiements par carte',
    }
  })()

  const renderInstallmentRow = (installment: OverviewInstallment) => {
    const status = statusLabel(installment.status, installment.payment_provider)
    return (
      <Box
        key={installment.id}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor:
            installment.status === 'FAILED'
              ? '#FDECEA'
              : ['SUBMITTED', 'VALIDATED'].includes(installment.status) &&
                  installment.payment_provider.toLowerCase() === 'gocardless'
                ? '#FFF8E1'
                : 'transparent',
          px: installment.status === 'FAILED' || installment.status === 'SUBMITTED' ? 1.5 : 0,
          mx: installment.status === 'FAILED' || installment.status === 'SUBMITTED' ? -1.5 : 0,
          borderRadius: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatDate(installment.due_date)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Échéance {installment.index} · {installment.provider_label}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="flex-end"
        >
          <Typography variant="body1" sx={{ fontWeight: 600, minWidth: 96, textAlign: { sm: 'right' } }}>
            {formatCurrency(installment.amount)}
          </Typography>
          <Typography variant="body2" sx={{ color: status.color, fontWeight: 700, minWidth: 120 }}>
            {status.label}
          </Typography>
          {installment.pay_now_eligible && (
            <Button
              size="small"
              variant={installment.status === 'FAILED' ? 'contained' : 'outlined'}
              color={installment.status === 'FAILED' ? 'error' : 'primary'}
              onClick={() => handlePayNow(installment.id)}
              disabled={payingId === installment.id || overview.schedule?.cancelled}
            >
              {payingId === installment.id ? <CircularProgress size={18} /> : 'Payer maintenant'}
            </Button>
          )}
        </Stack>
      </Box>
    )
  }

  return (
    <Layout form={mockForm} steps={mockSteps} wideContent>
      <Grid container spacing={3} sx={{ width: '100%', padding: { xs: 2, md: 3 } }}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Mon échéancier
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {overview.product_name
              ? `${overview.product_name}${
                  overview.schedule?.installments_count
                    ? ` · ${overview.schedule.installments_count} mensualité${
                        overview.schedule.installments_count > 1 ? 's' : ''
                      }`
                    : ''
                }`
              : 'Consultez vos paiements et gérez vos moyens de paiement.'}
          </Typography>
        </Grid>

        {mandateMissing && (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              sx={alertSx('warning')}
              action={
                <Button
                  color="inherit"
                  variant="contained"
                  size="small"
                  onClick={() => startMandateFlow(false)}
                  disabled={mandateLoading}
                  sx={{ bgcolor: '#ED6C02', color: 'white', '&:hover': { bgcolor: '#C77700' }, whiteSpace: 'nowrap' }}
                >
                  Configurer mon mandat
                </Button>
              }
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Action requise : mandat SEPA manquant
              </Typography>
              <Typography variant="body2">
                Votre mandat de prélèvement n&apos;est pas encore configuré. Les prochaines échéances SEPA échoueront
                sans action de votre part.
              </Typography>
            </Alert>
          </Grid>
        )}

        {dueFailures.length > 0 && (
          <Grid item xs={12}>
            <Alert
              severity="error"
              sx={alertSx('error')}
              action={
                !showCardSection ? (
                  <Button
                    color="inherit"
                    variant="outlined"
                    size="small"
                    onClick={handleUpdatePaymentMethod}
                    disabled={loadingCard}
                    sx={{ borderColor: '#D32F2F', color: '#D32F2F', whiteSpace: 'nowrap' }}
                  >
                    Mettre à jour ma carte
                  </Button>
                ) : undefined
              }
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {dueFailures.length === 1 ? 'Un paiement est en échec' : 'Des paiements sont en échec'}
              </Typography>
              <Typography variant="body2">
                Réglez l&apos;échéance concernée avec le bouton « Payer maintenant », ou mettez à jour votre carte si
                le paiement est refusé.
              </Typography>
            </Alert>
          </Grid>
        )}

        {sepaInFlight.length > 0 && dueFailures.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info" sx={alertSx('info')}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Prélèvement SEPA en cours
              </Typography>
              <Typography variant="body2">
                {sepaInFlight.length === 1
                  ? `Un prélèvement de ${formatCurrency(sepaInFlight[0].amount)} a été déclenché pour le ${formatDate(sepaInFlight[0].due_date)}. Le montant apparaîtra sur votre compte bancaire sous quelques jours ouvrés.`
                  : 'Des prélèvements SEPA ont été déclenchés. Les montants apparaîtront sur votre compte bancaire sous quelques jours ouvrés.'}
              </Typography>
            </Alert>
          </Grid>
        )}

        {overview.schedule?.cancelled && (
          <Grid item xs={12}>
            <Alert severity="error" sx={alertSx('error')}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Échéancier annulé
              </Typography>
              <Typography variant="body2">
                Contactez-nous {tunnelBrand() ? 'à' : 'au'} {contact} pour débloquer la situation.
              </Typography>
            </Alert>
          </Grid>
        )}

        {mandateConfirming && (
          <Grid item xs={12}>
            <Alert severity="info" sx={alertSx('info')} icon={<CircularProgress size={22} />}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Confirmation du mandat SEPA…
              </Typography>
              <Typography variant="body2">
                Nous attendons la confirmation de votre banque. Cela ne prend généralement que quelques secondes.
              </Typography>
            </Alert>
          </Grid>
        )}

        {mandateTimedOut && (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              sx={alertSx('warning')}
              action={
                <Button
                  color="inherit"
                  variant="contained"
                  size="small"
                  onClick={() => startMandateFlow(false)}
                  disabled={mandateLoading}
                  sx={{ bgcolor: '#ED6C02', color: 'white', '&:hover': { bgcolor: '#C77700' } }}
                >
                  Réessayer
                </Button>
              }
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Confirmation du mandat en attente
              </Typography>
              <Typography variant="body2">
                Nous n&apos;avons pas encore reçu la confirmation. Réessayez, ou actualisez la page dans un instant.
              </Typography>
            </Alert>
          </Grid>
        )}

        {actionError && (
          <Grid item xs={12}>
            <Alert severity="error" sx={alertSx('error')} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          </Grid>
        )}
        {actionSuccess && (
          <Grid item xs={12}>
            <Alert severity="success" sx={alertSx('success')} onClose={() => setActionSuccess(null)}>
              {actionSuccess}
            </Alert>
          </Grid>
        )}

        {overview.schedule && (
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                gap: 3,
                py: 2,
              }}
            >
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Prochain paiement
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {overview.next_installment
                    ? formatCurrency(overview.next_installment.amount)
                    : '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {overview.next_installment
                    ? `${formatDate(overview.next_installment.due_date)} · ${overview.next_installment.provider_label}`
                    : 'Aucun paiement restant'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Restant à payer
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatCurrency(overview.schedule.remaining_amount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  sur {formatCurrency(overview.schedule.total_amount || 0)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Moyen de paiement
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                  {paymentMethodSummary.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {paymentMethodSummary.subtitle}
                </Typography>
              </Box>
            </Box>
            <Divider />
          </Grid>
        )}

        {showPaymentMethodsSection && (
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Moyens de paiement
            </Typography>

            {overview.is_gocardless && (
              <Box
                sx={{
                  border: `1px solid ${mandateMissing ? '#ED6C02' : theme.palette.divider}`,
                  borderRadius: 1,
                  p: 2.5,
                  mb: showCardSection ? 2 : 0,
                  backgroundColor: mandateMissing ? '#FFF4E5' : 'transparent',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Prélèvements SEPA
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Vos échéances après le premier paiement sont prélevées automatiquement sur votre compte bancaire.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {overview.mandate?.ready
                    ? [
                        overview.mandate.bank_name || 'Banque configurée',
                        overview.mandate.account_number_ending
                          ? `compte se terminant par ${overview.mandate.account_number_ending}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : 'Banque : non configurée'}
                </Typography>
                {overview.mandate?.ready && overview.mandate.earliest_safe_charge_date && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Premier prélèvement possible à partir du{' '}
                    {formatDate(overview.mandate.earliest_safe_charge_date)}.
                  </Typography>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="contained"
                    onClick={() => startMandateFlow(Boolean(overview.mandate?.ready))}
                    disabled={mandateLoading || mandateConfirming}
                  >
                    {mandateLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : overview.mandate?.ready ? (
                      'Reconfigurer mon mandat SEPA'
                    ) : (
                      'Configurer mon mandat SEPA'
                    )}
                  </Button>
                </Stack>
              </Box>
            )}

            {showCardSection && (
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 2.5,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Carte bancaire
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Utilisée pour le premier paiement et pour régler une échéance immédiatement.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {cardLabel}
                </Typography>
                <Button variant="outlined" onClick={handleUpdatePaymentMethod} disabled={loadingCard}>
                  {loadingCard ? <CircularProgress size={20} /> : 'Mettre à jour ma carte'}
                </Button>
              </Box>
            )}
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Paiements
          </Typography>

          {upcomingInstallments.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                À venir
              </Typography>
              {upcomingInstallments.map(renderInstallmentRow)}
            </Box>
          )}

          {pastInstallments.length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Passés
              </Typography>
              {pastInstallments.map(renderInstallmentRow)}
            </Box>
          )}

          {!overview.installments.length && (
            <Typography variant="body2" color="text.secondary">
              Aucun paiement à afficher pour le moment.
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Une question ? {tunnelBrand() ? 'Écrivez-nous à' : 'Appelez-nous au'} {contact}.
          </Typography>
        </Grid>
      </Grid>

      <StripeModal
        stripeSession={stripeSession}
        open={open}
        setOpen={setOpen}
        redirectUrl={`${window.location.origin}/mon-echeancier/validation?global_id=${global_id}&had_failed_payment=${
          overview.installments.some((installment) => installment.status === 'FAILED') ? '1' : '0'
        }`}
      />
    </Layout>
  )
}

export default UpdatePaymentMethod
